<template>
	<div class="information-container" :class="{'adjust-bottom-spacing': getMobileAppState}">
        <div class="title">{{t('userArea.definitions.secureAssociationEnrollment.title')}}</div>
        <div class="sub-title">{{t('userArea.definitions.secureAssociationEnrollment.subTitle')}}</div>
        <div class="list-title">{{t('userArea.definitions.secureAssociationEnrollment.listTitle')}}</div>
        <!-- Activate List -->
        <text-block-list style="max-width: 508px; margin-bottom: 24px;" :list="list"/>
        <!-- Info -->
        <infobox-generic
        v-if="getMobileAppState"
        style="margin-bottom: 24px;"
        :label="infoboxLabel"/>
        <!-- Device Status -->
        <device-status-info-box
        :key="deviceInformationParsed"
        :label="t('userArea.definitions.secureAssociationEnrollment.state')"
        :contentInfo="deviceInformationParsed"
        @buttonClicked="emitHandler"/>
	</div>
</template>

<script setup>
import TextBlockList from '@/components/generic/textBlocks/List'
import DeviceStatusInfoBox from '@/components/features/userArea/definitions/secureAssociationEnrollment/DeviceStatusInfoBox'
import InfoboxGeneric from '@/components/generic/infobox/Generic'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* Assets & Helpers */
import { ref, computed } from 'vue'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapGetters */
const { getRememberMeEntityInfo, getAllCustomerInfo } = mapGetters(moduleMapper.SESSION)
const { getMobileAppState } = mapGetters()

/* Props received:
@deviceInformation { Object }
@isInitialAction { Boolean }
*/
const props = defineProps({
    deviceInformation: Object,
    isInitialAction: Boolean
})

/* Emmitters */
const emit = defineEmits(['deactivateDevice', 'activateDevice'])

const activationDays = ref(180)

const selectedAcession = computed(() => {
    return getRememberMeEntityInfo.value.listOfAcessions.find(el => el.acessionNumber === getAllCustomerInfo.value.acessionNumber)
})

const showBiometricOption = computed(() => {
    return getMobileAppState.value && getRememberMeEntityInfo.value && selectedAcession.value.hasBiometricEnrolled
})

const infoboxLabel = computed(() => {
    return t('userArea.definitions.secureAssociationEnrollment.infobox.initialActionInfo', { activationDays: activationDays.value })
})

const list = ref([
    t('userArea.definitions.secureAssociationEnrollment.list.list1'),
    t('userArea.definitions.secureAssociationEnrollment.list.list2')
])

const deviceInformationParsed = computed(() => {
    const deviceResponseInfo = props.deviceInformation
    const parsedInfo = []

    /* If we have an active device */
    if (deviceResponseInfo.alreadyHasActiveSecureAssociation && deviceResponseInfo.clientChannelDevice) {
        /* If active device is not the current device */
        if (deviceResponseInfo.clientChannelDevice && (!getMobileAppState.value || deviceResponseInfo.clientChannelDevice.id !== window.device.uuid)) {
            const model = deviceResponseInfo.clientChannelDevice.model ? deviceResponseInfo.clientChannelDevice.model : ''
            const system = deviceResponseInfo.clientChannelDevice.system ? deviceResponseInfo.clientChannelDevice.system : ''
            const systemVersion = deviceResponseInfo.clientChannelDevice.systemVersion ? deviceResponseInfo.clientChannelDevice.systemVersion : ''

            parsedInfo.push({
                id: deviceResponseInfo.id,
                info: t('userArea.definitions.secureAssociationEnrollment.activeInOtherDevice', { device: `${model}, ${system} ${systemVersion}` }),
                status: 'success',
                buttonLabel: t('userArea.definitions.secureAssociationEnrollment.deactivate'),
                modalTitle: t('userArea.definitions.secureAssociationEnrollment.confirmationModal.deactivateTitleOtherDevice'),
                modalText: t('userArea.definitions.secureAssociationEnrollment.confirmationModal.deactivateTextOtherDevice'),
                system: deviceResponseInfo.clientChannelDevice.system
            })

            if (getMobileAppState.value && showBiometricOption.value) {
                parsedInfo.push({
                    id: window.device.uuid,
                    info: t('userArea.definitions.secureAssociationEnrollment.thisDeviceInactive'),
                    status: 'inactive',
                    buttonLabel: t('userArea.definitions.secureAssociationEnrollment.activate'),
                    modalTitle: t('userArea.definitions.secureAssociationEnrollment.confirmationModal.activateTitle'),
                    modalText: t('userArea.definitions.secureAssociationEnrollment.confirmationModal.activateTextOtherDevice'),
                    system: window.device.platform
                })
            }
        } else {
            parsedInfo.push({
                id: deviceResponseInfo.id,
                info: t('userArea.definitions.secureAssociationEnrollment.activeInThisDevice'),
                status: 'success',
                buttonLabel: t('userArea.definitions.secureAssociationEnrollment.deactivate'),
                modalTitle: t('userArea.definitions.secureAssociationEnrollment.confirmationModal.deactivateTitleSameDevice'),
                modalText: t('userArea.definitions.secureAssociationEnrollment.confirmationModal.deactivateTextSameDevice'),
                system: deviceResponseInfo.clientChannelDevice.system
            })
        }
    }

    /* If fulfill requirements and there is no current active device active */
    if (getMobileAppState.value &&
        showBiometricOption.value &&
        !deviceResponseInfo.alreadyHasActiveSecureAssociation) {
            parsedInfo.push({
                id: window.device.uuid,
                info: t('userArea.definitions.secureAssociationEnrollment.inactive'),
                status: 'inactive',
                buttonLabel: t('userArea.definitions.secureAssociationEnrollment.activate'),
                modalTitle: t('userArea.definitions.secureAssociationEnrollment.confirmationModal.activateTitle'),
                modalText: t('userArea.definitions.secureAssociationEnrollment.confirmationModal.activateText'),
                system: window.device.platform
            })
    }

    /* If fulfill requirements and current device is active */
    if (getMobileAppState.value &&
        showBiometricOption.value &&
        deviceResponseInfo.alreadyHasActiveSecureAssociation &&
        !deviceResponseInfo.clientChannelDevice) {
            parsedInfo.push({
                id: window.device.uuid,
                info: t('userArea.definitions.secureAssociationEnrollment.activeInThisDevice'),
                status: 'success',
                buttonLabel: t('userArea.definitions.secureAssociationEnrollment.deactivate'),
                modalTitle: t('userArea.definitions.secureAssociationEnrollment.confirmationModal.deactivateTitleSameDevice'),
                modalText: t('userArea.definitions.secureAssociationEnrollment.confirmationModal.deactivateTextSameDevice'),
                system: window.device.platform
            })
    }

    /* If Web or device doesnt fulfill requirements for association */
    if (!getMobileAppState.value || !showBiometricOption.value) {
        parsedInfo.push({
            id: getMobileAppState.value && window.device ? window.device.uuid : 1,
            info: t('userArea.definitions.secureAssociationEnrollment.unavailable'),
            status: 'unavailable',
            buttonLabel: getMobileAppState.value ? t('userArea.definitions.secureAssociationEnrollment.activate') : null,
            system: getMobileAppState.value && window.device ? window.device.platform : null
        })
    }

    return parsedInfo
})

const emitHandler = event => {
    let deviceInfo = deviceInformationParsed.value.find(device => device.id === event)

    if (getMobileAppState.value) {
        deviceInfo = {
            ...deviceInfo,
            systemVersion: window.device.version,
            vendor: window.device.manufacturer,
            model: window.device.model
        }
    }

    deviceInfo.status === 'success' ? emit('deactivateDevice', { deviceInfo: deviceInfo }) : emit('activateDevice', { deviceInfo: deviceInfo })
}

</script>

<style lang="scss" scoped>
.information-container {
    width: 100%;

    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;

    &.adjust-bottom-spacing {
        margin-bottom: 75px;
    }

    .title {
        width: 100%;
        max-width: 508px;
        margin-bottom: 8px;

        line-height: 20px;
        color: var(--primary);
        @include H5-Medium;
    }

    .sub-title {
        width: 100%;
        max-width: 508px;
        margin-bottom: 24px;

        line-height: 18px;
        color: var(--secondary-dark-3);
        @include H6-Regular;
    }

    .list-title {
        width: 100%;
        max-width: 508px;
        margin-bottom: 8px;

        line-height: 18px;
        color: var(--secondary-dark-3);
        @include H6-Medium;
    }
}
</style>
